/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useRef } from "react"
import type { RefObject } from "react"

import { animate, useReducedMotion } from "framer-motion"
import eq from "lodash-es/eq"
import isNull from "lodash-es/isNull"
import { useSearchParams } from "react-router-dom"
import { useIsomorphicLayoutEffect } from "react-use"

import type { TEmptyCallback } from "@/shared/types/functions"

import { coachingChatUiConfig as uiConfig } from "../config"
import { useCoachingChatConversationStatusSelector as useConversationStatusSelector } from "../store"
import { ECoachingChatConversationStatus as EConversationStatus } from "../types"
import { coachingChatUiUtils as uiUtils } from "../utils"

const { autoScrollToSection } = uiUtils
const { CHAT_AUTOSCROLL_SEARCH_PARAM_NAME: SEARCH_PARAM_NAME, CHAT_AUTOSCROLL_SEARCH_PARAM_VALUE: SEARCH_PARAM_VALUE } =
    uiConfig

function useCoachingChatAutoscroller(entityId: number): ReturnType<
    (entityId: number) => {
        formRef: RefObject<HTMLDivElement>
        chatRef: RefObject<HTMLDivElement>
        feedRef: RefObject<HTMLDivElement>
    }
> {
    const isReducedMotion: boolean = useReducedMotion()

    const [searchParams]: ReturnType<typeof useSearchParams> = useSearchParams()

    const conversationStatus: EConversationStatus = useConversationStatusSelector(entityId)

    const formRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)
    const chatRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)
    const feedRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const isAutoScrolled: RefObject<boolean> = useRef<boolean>(false)

    useIsomorphicLayoutEffect(
        (): void =>
            eq(searchParams.get(SEARCH_PARAM_NAME), SEARCH_PARAM_VALUE) && !isAutoScrolled.current
                ? autoScrollToSection({
                      status: conversationStatus,
                      chatRef,
                      formRef,
                      onScrollComplete: (): void => ((isAutoScrolled.current = true), void 0),
                      isReducedMotion
                  })
                : void 0,
        [conversationStatus, searchParams, isReducedMotion]
    )

    return { formRef, chatRef, feedRef }
}

function useCoachingChatFeedScroller(feedRef: RefObject<HTMLDivElement>): TEmptyCallback {
    const isMotionReduced: boolean = useReducedMotion()

    return useCallback((): void => {
        const element: HTMLDivElement = feedRef.current
        if (isNull(element)) return void 0

        animate(element.scrollTop, element.scrollHeight, {
            duration: isMotionReduced ? 0 : 0.5,
            type: "spring",
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (latest: number): void => void (element.scrollTop = latest)
        })

        return void 0
    }, [isMotionReduced])
}

export { useCoachingChatAutoscroller, useCoachingChatFeedScroller }
